<template>
  <div class="mission-page">
    <PrePeersInstitutionalPageHero
      :title="t('notreMission.hero.title')"
      :highlighted-title-part="t('notreMission.hero.highlightedTitlePart')"
      :breadcrumb-label="t('notreMission.breadcrumb')">
      <div class="mission-page__hero-leads">
        <p class="mission-page__hero-lead">{{ t('notreMission.hero.lead') }}</p>
        <p class="mission-page__hero-lead mission-page__hero-lead--closing">
          {{ t('notreMission.hero.leadClosing') }}
        </p>
      </div>
    </PrePeersInstitutionalPageHero>

    <section class="mission-page__band mission-page__band--first">
      <div class="mission-page__container mission-page__stack">
        <PrePeersVideoCard
          :youtube-id="MISSION_VIDEO_YOUTUBE_ID"
          :video-title="t('notreMission.video.title')"
          :caption-title="t('notreMission.video.captionTitle')"
          :caption-text="t('notreMission.video.captionText')"
          @open="trackMissionVideoOpening" />

        <div class="mission-page__section">
          <h2 class="mission-page__heading">{{ t('notreMission.purpose.title') }}</h2>
          <p class="mission-page__accent">{{ t('notreMission.purpose.accent') }}</p>
          <p class="mission-page__text">{{ t('notreMission.purpose.selfKnowledge') }}</p>
          <p class="mission-page__text">{{ t('notreMission.purpose.recommendations') }}</p>
          <p class="mission-page__text">{{ t('notreMission.purpose.experience') }}</p>
          <PrePeersLolaCta variant="institutional" />
        </div>
      </div>
    </section>

    <section class="mission-page__band mission-page__band--alt">
      <div class="mission-page__container mission-page__section">
        <h2 class="mission-page__heading">{{ t('notreMission.reasonForBeing.title') }}</h2>
        <p class="mission-page__text">{{ t('notreMission.reasonForBeing.intro') }}</p>
        <div class="mission-page__stats">
          <PrePeersMetricCard
            v-for="stat in reasonForBeingStats"
            :key="stat.Name"
            :metric="stat"
            has-gradient-value />
        </div>
        <p class="mission-page__text">
          {{ t('notreMission.reasonForBeing.selfKnowledgeProblem') }}
        </p>
        <p class="mission-page__accent">{{ t('notreMission.reasonForBeing.accent') }}</p>
      </div>
    </section>

    <section class="mission-page__band">
      <div class="mission-page__container mission-page__section">
        <h2 class="mission-page__heading">{{ t('notreMission.whatWeDo.title') }}</h2>
        <p class="mission-page__text">{{ t('notreMission.whatWeDo.discovery') }}</p>
        <p class="mission-page__text">{{ t('notreMission.whatWeDo.opportunities') }}</p>
        <p class="mission-page__accent">{{ t('notreMission.whatWeDo.accent') }}</p>
      </div>
    </section>

    <section class="mission-page__band mission-page__band--alt">
      <div class="mission-page__container mission-page__section">
        <span class="badge badge-blue mission-page__tag">
          {{ t('notreMission.missionCompany.tag') }}
        </span>
        <h2 class="mission-page__heading">{{ t('notreMission.missionCompany.title') }}</h2>
        <p class="mission-page__text">{{ t('notreMission.missionCompany.intro') }}</p>

        <p class="mission-page__label">{{ t('notreMission.missionCompany.purposeLabel') }}</p>
        <blockquote class="mission-page__quote">
          {{ t('notreMission.missionCompany.purposeQuote') }}
        </blockquote>
        <p class="mission-page__fine-print">
          {{ t('notreMission.missionCompany.purposeExplanation') }}
        </p>

        <p class="mission-page__label">{{ t('notreMission.missionCompany.objectivesLabel') }}</p>
        <ul class="mission-page__check-list">
          <li class="mission-page__check-item">
            <PrepeersSelectionCheck class="mission-page__check-icon" />
            <span>
              <strong>{{ t('notreMission.missionCompany.objectives.innovationLabel') }}</strong>
              {{ t('notreMission.missionCompany.objectives.innovationText') }}
            </span>
          </li>
          <li class="mission-page__check-item">
            <PrepeersSelectionCheck class="mission-page__check-icon" />
            <span>
              <strong>{{ t('notreMission.missionCompany.objectives.emulationLabel') }}</strong>
              {{ t('notreMission.missionCompany.objectives.emulationText') }}
            </span>
          </li>
          <li class="mission-page__check-item">
            <PrepeersSelectionCheck class="mission-page__check-icon" />
            <span>
              <strong>{{ t('notreMission.missionCompany.objectives.solidarityLabel') }}</strong>
              {{ t('notreMission.missionCompany.objectives.solidarityText') }}
            </span>
          </li>
          <li class="mission-page__check-item">
            <PrepeersSelectionCheck class="mission-page__check-icon" />
            <span>
              <strong>{{ t('notreMission.missionCompany.objectives.efficiencyLabel') }}</strong>
              {{ t('notreMission.missionCompany.objectives.efficiencyText') }}
            </span>
          </li>
        </ul>

        <p class="mission-page__label">{{ t('notreMission.missionCompany.commitmentsIntro') }}</p>
        <div class="mission-page__accordions">
          <AccordionSection
            default-open
            :title="t('notreMission.missionCompany.freeForever.title')">
            <template #prefix>
              <span class="mission-page__number">1</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.missionCompany.freeForever.text') }}
            </p>
          </AccordionSection>
          <AccordionSection
            default-open
            :title="t('notreMission.missionCompany.algorithmNotForSale.title')">
            <template #prefix>
              <span class="mission-page__number">2</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.missionCompany.algorithmNotForSale.text') }}
            </p>
            <p class="mission-page__text">
              <I18nT
                keypath="notreMission.missionCompany.algorithmNotForSale.nonPartner"
                scope="global">
                <template #howLolaDecidesLink>
                  <NuxtLink
                    :to="localePath('/comment-lola-decide')"
                    class="mission-page__link"
                    >{{
                      t('notreMission.missionCompany.algorithmNotForSale.howLolaDecidesLink')
                    }}</NuxtLink
                  >
                </template>
              </I18nT>
            </p>
          </AccordionSection>
          <AccordionSection
            default-open
            :title="t('notreMission.missionCompany.dataNotForSale.title')">
            <template #prefix>
              <span class="mission-page__number">3</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.missionCompany.dataNotForSale.noSale') }}
            </p>
            <p class="mission-page__text">
              {{ t('notreMission.missionCompany.dataNotForSale.consentSplit') }}
            </p>
            <p class="mission-page__text">
              {{ t('notreMission.missionCompany.dataNotForSale.rights') }}
            </p>
            <p class="mission-page__text">
              {{ t('notreMission.missionCompany.dataNotForSale.ownership') }}
            </p>
          </AccordionSection>
        </div>

        <p class="mission-page__text">
          <I18nT
            keypath="notreMission.missionCompany.profileVisibility"
            scope="global">
            <template #howLolaDecidesLink>
              <NuxtLink
                :to="localePath('/comment-lola-decide')"
                class="mission-page__link"
                >{{ t('notreMission.missionCompany.profileVisibilityLink') }}</NuxtLink
              >
            </template>
          </I18nT>
        </p>
        <p class="mission-page__text">{{ t('notreMission.missionCompany.activation') }}</p>
        <p class="mission-page__text">{{ t('notreMission.missionCompany.noTransmission') }}</p>
        <p class="mission-page__text">{{ t('notreMission.missionCompany.protection') }}</p>
      </div>
    </section>

    <section class="mission-page__band">
      <div class="mission-page__container mission-page__section">
        <h2 class="mission-page__heading">{{ t('notreMission.missionCommittee.title') }}</h2>
        <p class="mission-page__text">{{ t('notreMission.missionCommittee.intro') }}</p>
        <p class="mission-page__text">{{ t('notreMission.missionCommittee.composition') }}</p>
        <ul class="mission-page__check-list">
          <li class="mission-page__check-item">
            <PrepeersSelectionCheck class="mission-page__check-icon" />
            <span>{{ t('notreMission.missionCommittee.employeeRepresentative') }}</span>
          </li>
          <li class="mission-page__check-item">
            <PrepeersSelectionCheck class="mission-page__check-icon" />
            <span>{{ t('notreMission.missionCommittee.nonProfitRepresentative') }}</span>
          </li>
          <li class="mission-page__check-item">
            <PrepeersSelectionCheck class="mission-page__check-icon" />
            <span>{{ t('notreMission.missionCommittee.corporateOfficer') }}</span>
          </li>
        </ul>
        <p class="mission-page__text">{{ t('notreMission.missionCommittee.missionReferent') }}</p>
      </div>
    </section>

    <section class="mission-page__band mission-page__band--alt">
      <div class="mission-page__container mission-page__section">
        <h2 class="mission-page__heading">{{ t('notreMission.impact.title') }}</h2>
        <p class="mission-page__text">{{ t('notreMission.impact.intro') }}</p>
        <ul class="mission-page__indicators">
          <li class="mission-page__indicator">
            <strong class="mission-page__indicator-title">
              {{ t('notreMission.impact.satisfactionTitle') }}
            </strong>
            <span class="mission-page__indicator-text">
              {{ t('notreMission.impact.satisfactionText') }}
            </span>
          </li>
          <li class="mission-page__indicator">
            <strong class="mission-page__indicator-title">
              {{ t('notreMission.impact.reorientationTitle') }}
            </strong>
            <span class="mission-page__indicator-text">
              {{ t('notreMission.impact.reorientationText') }}
            </span>
            <span class="mission-page__indicator-goal">
              {{ t('notreMission.impact.reorientationGoal') }}
            </span>
          </li>
          <li class="mission-page__indicator">
            <strong class="mission-page__indicator-title">
              {{ t('notreMission.impact.accessibilityTitle') }}
            </strong>
            <span class="mission-page__indicator-text">
              {{ t('notreMission.impact.accessibilityText') }}
            </span>
          </li>
          <li class="mission-page__indicator">
            <strong class="mission-page__indicator-title">
              {{ t('notreMission.impact.consentTitle') }}
            </strong>
            <span class="mission-page__indicator-text">
              {{ t('notreMission.impact.consentText') }}
            </span>
          </li>
        </ul>
      </div>
    </section>

    <section class="mission-page__band">
      <div class="mission-page__container mission-page__section">
        <h2 class="mission-page__heading">{{ t('notreMission.profitability.title') }}</h2>
        <p class="mission-page__text">{{ t('notreMission.profitability.question') }}</p>
        <p class="mission-page__text">{{ t('notreMission.profitability.trust') }}</p>
        <p class="mission-page__text">{{ t('notreMission.profitability.model') }}</p>
        <p class="mission-page__text">{{ t('notreMission.profitability.winWin') }}</p>
        <p class="mission-page__accent">{{ t('notreMission.profitability.accent') }}</p>
      </div>
    </section>

    <section class="mission-page__band mission-page__band--alt">
      <div class="mission-page__container mission-page__section">
        <h2 class="mission-page__heading">{{ t('notreMission.ambition.title') }}</h2>
        <ol class="mission-page__timeline">
          <li class="mission-page__timeline-step">
            <span class="mission-page__timeline-label text-gradient">
              {{ t('notreMission.ambition.threeYearsLabel') }}
            </span>
            <p class="mission-page__text">{{ t('notreMission.ambition.threeYearsText') }}</p>
          </li>
          <li class="mission-page__timeline-step">
            <span class="mission-page__timeline-label text-gradient">
              {{ t('notreMission.ambition.fiveYearsLabel') }}
            </span>
            <p class="mission-page__text">{{ t('notreMission.ambition.fiveYearsText') }}</p>
          </li>
          <li class="mission-page__timeline-step">
            <span class="mission-page__timeline-label text-gradient">
              {{ t('notreMission.ambition.tenYearsLabel') }}
            </span>
            <p class="mission-page__text">{{ t('notreMission.ambition.tenYearsText') }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="mission-page__band">
      <div class="mission-page__container mission-page__section">
        <h2 class="mission-page__heading">{{ t('notreMission.values.title') }}</h2>
        <p class="mission-page__text">{{ t('notreMission.values.intro') }}</p>
        <div class="mission-page__accordions">
          <AccordionSection
            default-open
            :title="t('notreMission.values.honesty.title')">
            <template #prefix>
              <span class="mission-page__number">1</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.values.honesty.noEndlessResults') }}
            </p>
            <p class="mission-page__text">
              {{ t('notreMission.values.honesty.noFeaturedPlaces') }}
            </p>
            <p class="mission-page__text">{{ t('notreMission.values.honesty.truthFirst') }}</p>
          </AccordionSection>
          <AccordionSection
            default-open
            :title="t('notreMission.values.selfKnowledge.title')">
            <template #prefix>
              <span class="mission-page__number">2</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.values.selfKnowledge.otherPlatforms') }}
            </p>
            <p class="mission-page__text">{{ t('notreMission.values.selfKnowledge.ourStart') }}</p>
            <p class="mission-page__text">
              {{ t('notreMission.values.selfKnowledge.dataConviction') }}
            </p>
          </AccordionSection>
          <AccordionSection
            default-open
            :title="t('notreMission.values.simplicity.title')">
            <template #prefix>
              <span class="mission-page__number">3</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.values.simplicity.threeRightOnes') }}
            </p>
            <p class="mission-page__text">
              <I18nT
                keypath="notreMission.values.simplicity.featureQuestion"
                scope="global">
                <template #question>
                  <em>{{ t('notreMission.values.simplicity.featureQuestionText') }}</em>
                </template>
              </I18nT>
            </p>
            <p class="mission-page__text">{{ t('notreMission.values.simplicity.notLaziness') }}</p>
          </AccordionSection>
          <AccordionSection
            default-open
            :title="t('notreMission.values.studentDecides.title')">
            <template #prefix>
              <span class="mission-page__number">4</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.values.studentDecides.lolaRecommends') }}
            </p>
            <p class="mission-page__text">
              {{ t('notreMission.values.studentDecides.yourChoice') }}
            </p>
            <p class="mission-page__text">
              {{ t('notreMission.values.studentDecides.noAlgorithmKnowsBetter') }}
            </p>
          </AccordionSection>
          <AccordionSection
            default-open
            :title="t('notreMission.values.accessibility.title')">
            <template #prefix>
              <span class="mission-page__number">5</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.values.accessibility.forEveryone') }}
            </p>
            <p class="mission-page__text">{{ t('notreMission.values.accessibility.measured') }}</p>
          </AccordionSection>
          <AccordionSection
            default-open
            :title="t('notreMission.values.transparency.title')">
            <template #prefix>
              <span class="mission-page__number">6</span>
            </template>
            <p class="mission-page__text">
              {{ t('notreMission.values.transparency.whatWeTellYou') }}
            </p>
            <p class="mission-page__text">
              {{ t('notreMission.values.transparency.changesFirst') }}
            </p>
          </AccordionSection>
        </div>

        <PrePeersLolaCta variant="institutional" />

        <p class="mission-page__fine-print">
          <I18nT
            keypath="notreMission.howLolaDecides.intro"
            scope="global">
            <template #link>
              <NuxtLink
                :to="localePath('/comment-lola-decide')"
                class="mission-page__link"
                >{{ t('notreMission.howLolaDecides.link') }}</NuxtLink
              >
            </template>
          </I18nT>
        </p>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, Ref } from 'vue'
import type { UseTrackingReturn } from '~/composables/useTracking'
import type { MetricObj } from '~/types/school'
import type { UseSeoReturn } from '~/types/UseSeo'
import { computed } from 'vue'

import PrePeersVideoCard from '~/components/cards/PrePeersVideoCard.vue'
import AccordionSection from '~/components/ui/AccordionSection.vue'
import PrePeersLolaCta from '~/components/ui/PrePeersLolaCta.vue'
import PrePeersMetricCard from '~/components/ui/PrePeersMetricCard.vue'
import PrepeersSelectionCheck from '~/components/ui/PrepeersSelectionCheck.vue'
import PrePeersInstitutionalPageHero from '~/components/sections/PrePeersInstitutionalPageHero.vue'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'

const MISSION_VIDEO_YOUTUBE_ID: string = 'fbbxrxH6X4s'
const CONTACT_EMAIL: string = 'info@prepeers.co'
const FOUNDING_YEAR: string = '2019'

const { t, locale }: { t: (key: string) => string; locale: Ref<string> } = useI18n()
const localePath: ReturnType<typeof useLocalePath> = useLocalePath()
const { siteUrl, canonicalUrl, absoluteUrl }: UseSeoReturn = useSeo()
const { track }: UseTrackingReturn = useTracking()

definePageMeta({
  layout: 'default',
})

const reasonForBeingStats: ComputedRef<MetricObj[]> = computed((): MetricObj[] => [
  {
    Value: t('notreMission.reasonForBeing.lostStudentsValue'),
    Name: t('notreMission.reasonForBeing.lostStudentsLabel'),
  },
  {
    Value: t('notreMission.reasonForBeing.regretValue'),
    Name: t('notreMission.reasonForBeing.regretLabel'),
  },
  {
    Value: t('notreMission.reasonForBeing.programsValue'),
    Name: t('notreMission.reasonForBeing.programsLabel'),
  },
])

/**
 * Trace l'ouverture de la vidéo de la mission dans PostHog.
 * @returns {void}
 */
function trackMissionVideoOpening(): void {
  track(TRACKING_EVENTS.PRESENTATION_VIDEO_OPENED, { video: 'mission' })
}

useSeoMeta({
  title: computed((): string => t('notreMission.seo.title')),
  description: computed((): string => t('notreMission.seo.description')),
  ogTitle: computed((): string => t('notreMission.seo.title')),
  ogDescription: computed((): string => t('notreMission.seo.description')),
})

useJsonLd(
  computed(
    (): Record<string, unknown> => ({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'PrePeers', item: siteUrl.value },
            {
              '@type': 'ListItem',
              position: 2,
              name: t('notreMission.breadcrumb'),
              item: canonicalUrl.value,
            },
          ],
        },
        {
          '@type': 'AboutPage',
          name: t('notreMission.seo.title'),
          url: canonicalUrl.value,
          description: t('notreMission.seo.description'),
          inLanguage: locale.value,
          mainEntity: {
            '@type': 'Organization',
            name: 'PrePeers',
            url: siteUrl.value,
            logo: absoluteUrl('/logo_prepeers.svg'),
            foundingDate: FOUNDING_YEAR,
            email: CONTACT_EMAIL,
            ethicsPolicy: canonicalUrl.value,
          },
        },
      ],
    }),
  ),
)
</script>

<style scoped>
.mission-page__hero-leads {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.mission-page__hero-lead {
  font-size: var(--text-lg);
  font-weight: 600;
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
}

.mission-page__hero-lead--closing {
  font-size: var(--text-base);
  font-weight: 500;
}

.mission-page__band {
  padding-block: var(--space-16);
}

.mission-page__band--first {
  padding-top: var(--space-10);
}

.mission-page__band--alt {
  background: var(--bg-surface);
  border-block: 1px solid var(--border-subtle);
}

.mission-page__container {
  width: 100%;
  max-width: min(var(--container-lg), calc(100% - 2 * var(--space-6)));
  margin-inline: auto;
}

.mission-page__stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.mission-page__section {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.mission-page__heading {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: 700;
  line-height: var(--leading-snug);
  color: var(--text-primary);
}

.mission-page__accent {
  font-size: var(--text-lg);
  font-weight: 700;
  line-height: var(--leading-snug);
  color: var(--text-primary);
}

.mission-page__text {
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
}

.mission-page__label {
  margin-top: var(--space-2);
  font-weight: 700;
  color: var(--text-primary);
}

.mission-page__fine-print {
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  color: var(--text-muted);
}

.mission-page__link {
  font-weight: 600;
  color: var(--accent-blue-lt);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.mission-page__link:hover {
  color: var(--text-primary);
}

.mission-page__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
}

.mission-page__tag {
  align-self: flex-start;
}

.mission-page__quote {
  margin: 0;
  padding: var(--space-5) var(--space-6);
  border-left: 3px solid var(--accent-blue);
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
  background: var(--bg-elevated);
  font-size: var(--text-md);
  font-style: italic;
  line-height: var(--leading-relaxed);
  color: var(--text-primary);
}

.mission-page__check-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  list-style: none;
}

.mission-page__check-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
}

.mission-page__check-item strong {
  color: var(--text-primary);
}

.mission-page__check-icon {
  margin-top: 5px;
}

.mission-page__accordions {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.mission-page__accordions :deep(.accordion__title) {
  flex: 1;
  font-size: var(--text-base);
}

.mission-page__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background: var(--bg-elevated);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--accent-blue-lt);
}

.mission-page__indicators {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
  list-style: none;
}

.mission-page__indicator {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-5);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  background: var(--bg-base);
}

.mission-page__indicator-title {
  font-weight: 700;
  line-height: var(--leading-snug);
  color: var(--text-primary);
}

.mission-page__indicator-text {
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
}

.mission-page__indicator-goal {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--accent-blue-lt);
}

.mission-page__timeline {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-6);
  list-style: none;
}

.mission-page__timeline-step {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 2px solid var(--border-strong);
}

.mission-page__timeline-label {
  align-self: flex-start;
  font-family: var(--font-display);
  font-size: var(--text-lg);
  font-weight: 700;
}

@media (max-width: 768px) {
  .mission-page__hero-lead {
    font-size: var(--text-md);
  }

  .mission-page__band {
    padding-block: var(--space-12);
  }

  .mission-page__band--first {
    padding-top: var(--space-6);
  }

  .mission-page__stack {
    gap: var(--space-12);
  }

  .mission-page__heading {
    font-size: var(--text-xl);
  }

  .mission-page__stats,
  .mission-page__indicators,
  .mission-page__timeline {
    grid-template-columns: minmax(0, 1fr);
  }

  .mission-page__quote {
    padding: var(--space-4);
  }
}
</style>
